/*
 * mz-cart-count — the live cart badge the live header was missing.
 * ---------------------------------------------------------------------------
 * WHY THIS FILE EXISTS. The live header is `sections/mz-header.liquid`, not the
 * stock `sections/header.liquid`. Its cart control is a plain link whose count
 * (`{{ cart.item_count }}`) is baked in at page render, and the stock
 * `assets/cart-icon.js` (a `<cart-icon>` custom element) is NEVER instantiated
 * on this store — there is no `<cart-icon>` node in the live HTML. So the
 * studio's `_dispatchCartLinesUpdate` fires a real `shopify:cart:lines-update`
 * event into the void: nothing listens, and the badge only changes on a full
 * page load. This element IS that missing listener.
 *
 * DELIBERATELY DEPENDENCY-FREE. The stock component imports `@theme/component`
 * and `@shopify/events` — importmap aliases owned by the stock theme's private
 * contract. `mz-header` exists precisely to avoid depending on that (see its
 * header comment). So this is plain `HTMLElement`, plain `addEventListener`,
 * and the event name as a literal string. It reads the same fields the stock
 * component does (`cart.totalQuantity ?? detail.itemCount`) so the contract is
 * identical, without the coupling.
 *
 * PROGRESSIVE ENHANCEMENT. With JS off, the badge still renders its
 * server-rendered value and the link still works — this only keeps it live.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  // The standard event the studio (and the theme's own cart controls) fire.
  var CART_EVENT = 'shopify:cart:lines-update';
  var BADGE_SELECTOR = '[data-mz-cart-badge]';

  function toCount(value) {
    var n = Number(value);
    return Number.isFinite(n) ? Math.max(0, Math.trunc(n)) : null;
  }

  /* UNITS BOUGHT, not cart lines.
   *
   * Extra print placements are charged on their own hidden cart line, because a
   * non-Plus store cannot make a cart line cost more than `variant price x
   * quantity`. The cart folds those lines into the garment row, so counting them
   * here made one polo read as "2" in the badge — which looks like a broken cart
   * rather than a fee. Mirrors snippets/ds-cart-units.liquid; the two must agree
   * or the badge changes value on the first AJAX cart update. */
  function unitsIn(cart) {
    if (!cart || !Array.isArray(cart.items)) return cart && cart.item_count;
    return cart.items.reduce(function (n, it) {
      var props = it && it.properties;
      if (props && String(props._ds_surcharge) === '1') return n;
      return n + (Number(it && it.quantity) || 0);
    }, 0);
  }

  var MzCartCount = class extends HTMLElement {
    connectedCallback() {
      this.badge = this.querySelector(BADGE_SELECTOR);
      this.link = this.querySelector('a');
      this._onCartUpdate = this._onCartUpdate.bind(this);
      this._onPageShow = this._onPageShow.bind(this);
      document.addEventListener(CART_EVENT, this._onCartUpdate);
      // Back-button onto a bfcache'd page: the baked-in badge may be stale.
      window.addEventListener('pageshow', this._onPageShow);
    }

    disconnectedCallback() {
      document.removeEventListener(CART_EVENT, this._onCartUpdate);
      window.removeEventListener('pageshow', this._onPageShow);
    }

    /* ALWAYS RE-READ THE CART. Never the event's own count.
     *
     * This used to trust `cart.totalQuantity`, falling back to
     * `detail.itemCount` and only then to /cart.js. Both of those are RAW cart
     * quantities: `totalQuantity` comes straight off the AJAX cart, and
     * `itemCount` is the theme's `[ref="cartItemCount"]`, which renders
     * `cart.item_count`. Neither one skips the hidden surcharge lines. So the
     * badge agreed with `unitsIn()` on a full page load and then jumped the
     * moment anything touched the cart over AJAX — a polo with two extra
     * placements plus one other item read as SIX.
     *
     * /cart.js is one small request against a route the shopper has already
     * warmed, and it is the only source that carries line PROPERTIES, which is
     * what tells a garment apart from a fee. Correctness beats the round trip;
     * we are already inside one. */
    _onCartUpdate(event) {
      var promise = event && event.promise;
      var self = this;
      if (promise && typeof promise.then === 'function') {
        // Wait for the mutation to land, or /cart.js reads the pre-update cart.
        promise.then(function () { self._refetch(); }, function () { self._refetch(); });
      } else {
        this._refetch();
      }
    }

    _onPageShow(event) {
      if (event && event.persisted) this._refetch();
    }

    _refetch() {
      var self = this;
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (response) {
          return response && response.ok ? response.json() : null;
        })
        .then(function (cart) {
          var count = cart && toCount(unitsIn(cart));
          if (count !== null) self._setCount(count);
        })
        .catch(function () {
          /* leave the last-known value in place */
        });
    }

    _setCount(count) {
      var next = toCount(count);
      if (next === null) return;
      var prev = toCount(this.getAttribute('data-count'));
      this.setAttribute('data-count', String(next));

      if (!this.badge) this.badge = this.querySelector(BADGE_SELECTOR);
      if (this.badge) {
        this.badge.textContent = String(next);
        if (next === 0) {
          this.badge.setAttribute('hidden', '');
        } else {
          this.badge.removeAttribute('hidden');
          if (prev !== null && next > prev) this._pop();
        }
      }

      if (this.link) {
        this.link.setAttribute(
          'aria-label',
          next > 0 ? 'Cart, ' + next + ' item' + (next === 1 ? '' : 's') : 'Cart'
        );
      }
    }

    _pop() {
      var badge = this.badge;
      if (!badge) return;
      badge.classList.remove('mz-hd__badge--pop');
      // Force a reflow so the animation restarts on rapid successive adds.
      void badge.offsetWidth;
      badge.classList.add('mz-hd__badge--pop');
      badge.addEventListener(
        'animationend',
        function () {
          badge.classList.remove('mz-hd__badge--pop');
        },
        { once: true }
      );
    }
  };

  if ('customElements' in window && !customElements.get('mz-cart-count')) {
    customElements.define('mz-cart-count', MzCartCount);
  }
})();
